function ss(key) {
  if (typeof sessionStorage === "undefined") return "";
  const v = sessionStorage.getItem(key);
  return v && v !== "null" && v !== "undefined" ? v : "";
}

export function toLegacyGender(gender) {
  const g = String(gender || "").trim().toUpperCase();
  if (g === "MALE" || g === "M") return "M";
  if (g === "FEMALE" || g === "F") return "F";
  if (g === "OTHER" || g === "O" || g === "OTHERS") return "O";
  return gender || "";
}

export function parseDobParts(isoDob) {
  if (!isoDob || !String(isoDob).includes("-")) {
    return { year_of_birth: "", month_of_birth: "", day_of_birth: "", date_of_birth: "" };
  }
  const [year_of_birth, month_of_birth, day_of_birth] = String(isoDob).split("-");
  return {
    year_of_birth: year_of_birth || "",
    month_of_birth: month_of_birth || "",
    day_of_birth: day_of_birth || "",
    date_of_birth: day_of_birth && month_of_birth && year_of_birth
      ? `${day_of_birth}-${month_of_birth}-${year_of_birth}`
      : "",
  };
}

function normalizeBase64(value) {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (trimmed.startsWith("data:")) {
    const comma = trimmed.indexOf(",");
    return comma >= 0 ? trimmed.slice(comma + 1) : trimmed;
  }
  return trimmed;
}

function getContactValue(patient, channel) {
  const match = (patient.contacts || []).find(
    (c) => c.channel === channel && String(c.value || "").trim()
  );
  return match ? String(match.value).trim() : "";
}

function getPrimaryAddress(patient) {
  return (patient.addresses || []).find((a) => a.line1 || a.city || a.state) || patient.addresses?.[0] || {};
}

function resolvePatientId(patient, context = {}) {
  return (
    context.selectedOpdReferral?.patient_id
    || context.selectedPatientIdForSearch
    || patient.uhid
    || patient.patient_id
    || null
  );
}

const SOURCE_TYPE_VALUE_KEYS = {
  HOSPITAL: "hospitalName",
  MARKETING_PERSON: "marketingName",
  EMPLOYEE: "employeeName",
  EXTERNAL_DOCTOR: "referralDoctorId",
};

function buildPatientSource(source = {}, context = {}) {
  if (context.selectedOpdReferral) {
    const ref = context.selectedOpdReferral;
    return {
      source_type: "OPD_REFERRAL",
      source_type_value: ref.referred_doctor || ref.referred_by_doctor || ref.name || "",
    };
  }

  const sourceType = source.sourceType || "";
  if (!sourceType) {
    return {
      source_type: "WALK_IN",
      source_type_value: "Direct",
    };
  }

  const valueKey = SOURCE_TYPE_VALUE_KEYS[sourceType];
  return {
    source_type: sourceType,
    source_type_value: valueKey ? (source[valueKey] || "") : "",
  };
}

function mapFinancePayer(encounter = {}) {
  const payer = String(encounter.payer || "").trim();
  if (payer) return payer;
  if (encounter.financialClass === "CASH") return "self_pay";
  return "";
}

function mapDocument(doc = {}) {
  return {
    type: doc.type || "",
    number: doc.number || "",
    issued_by: doc.issued_by || doc.issuedBy || "",
    expiry: doc.expiry || "",
    file_name: doc.file_name || doc.fileName || "",
    file_base64: normalizeBase64(doc.file_base64 || doc.fileBase64 || ""),
  };
}

function mapContact(contact = {}) {
  return {
    channel: contact.channel || "",
    type: contact.type || "",
    value: contact.value || "",
  };
}

function mapAddress(address = {}) {
  return {
    type: address.type || "",
    line1: address.line1 || "",
    city: address.city || "",
    district: address.district || "",
    state: address.state || "",
    country: address.country || "",
    pincode: address.pincode || "",
  };
}

function mapFamilyMember(member = {}) {
  return {
    name: member.name || "",
    relation: member.relation || "",
    phone: member.phone || "",
    next_of_kin: Boolean(member.next_of_kin ?? member.nextOfKin),
  };
}

function mapGuarantor(guarantor = {}) {
  return {
    name: guarantor.name || "",
    relation: guarantor.relation || "",
    phone: guarantor.phone || "",
    id_type: guarantor.id_type || guarantor.idType || "",
    id_value: guarantor.id_value || guarantor.idValue || "",
  };
}

function mapEmergencyContact(contact = {}) {
  return {
    name: contact.name || "",
    relation: contact.relation || "",
    phone: contact.phone || "",
    address: contact.address || "",
  };
}

function buildAttendingDoctors(rows = []) {
  return rows
    .filter((row) => row.department && row.doctor_id)
    .map((row) => ({
      doctor_id: row.doctor_id,
      department_id: row.department,
      notes: row.notes || "",
    }));
}

/**
 * Builds ipd_admission/ POST payload — snake_case fields only, matching API contract.
 */
export function buildIpdAdmissionPayload(patient, context = {}) {
  const encounter = patient.encounter || {};
  const address = getPrimaryAddress(patient);
  const patientName = String(patient.name || "").trim();
  const dobParts = parseDobParts(patient.dob);
  const phoneNumber = patient.primaryMobile || getContactValue(patient, "PHONE");

  return {
    facility_id: ss("facility_name") || patient.facility || "",
    patient_id: resolvePatientId(patient, context),
    name: patientName,
    patient_name: patientName,
    gender: toLegacyGender(patient.gender),
    date_of_birth: dobParts.date_of_birth,
    year_of_birth: dobParts.year_of_birth,
    month_of_birth: dobParts.month_of_birth,
    day_of_birth: dobParts.day_of_birth,
    phone_number: phoneNumber,
    state: address.state || "",
    locality: address.line1 || "",
    pincode: address.pincode || "",
    city: address.city || "",
    district: address.district || "",
    country: address.country || "India",
    patient_type: "IPD",
    blood_group: patient.bloodGroup || "",
    assigned_doctor_id: encounter.admittingDoctorId || "",
    assigned_department: encounter.department || "",
    assigned_department_id: encounter.departmentId || "",
    bed_id: encounter.bedId || "",
    bed_type: encounter.wardNo || "",
    new_bed_status: encounter.newBedStatus || "reserved",
    patient_personal_detail: {
      title: patient.title || "",
      age_only: patient.ageOnly || "",
      marital_status: patient.maritalStatus || "",
      occupation: patient.occupation || "",
      nationality: patient.nationality || "",
      religion: patient.religion || "",
      identity_marks: patient.identityMarks || "",
      family_physician_name: patient.familyPhysicianName || "",
      family_physician_phone: patient.familyPhysicianPhone || "",
      secondary_mobile: patient.secondaryMobile || "",
      primary_mobile: phoneNumber,
    },
    finance_details: {
      admission_type: encounter.admissionType || "GENERAL",
      financial_class: encounter.financialClass || "CASH",
      payer: mapFinancePayer(encounter),
      employee_id: encounter.employeeId || "",
      insurance_policy_no: encounter.insurancePolicyNo || "",
      policy_expiry: encounter.policyExpiry || "",
    },
    patient_source: buildPatientSource(patient.source, context),
    documents: (patient.documents || []).map(mapDocument),
    contacts: (patient.contacts || []).map(mapContact),
    addresses: (patient.addresses || []).map(mapAddress),
    family_members: (patient.family || []).map(mapFamilyMember),
    guarantors: (patient.guarantors || []).map(mapGuarantor),
    emergency_contacts: (patient.emergencyContacts || []).map(mapEmergencyContact),
    attending_doctors: buildAttendingDoctors(encounter.attendingDoctorRows),
  };
}
